import { TFunction } from 'i18next';

import {
  AGENT_IDLE_STATE_DELAY_MS_MAX,
  AGENT_IDLE_STATE_DELAY_MS_MIN,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
  ExpandedAgentIdleState,
  ExpandedAgentIdleStateDraft,
  IdleStateValidationErrors,
} from '../model/idleState';

export type IdleStateValidationResult = {
  value: ExpandedAgentIdleState | null;
  errors: IdleStateValidationErrors;
};

const getOptionalNumberError = (
  t: TFunction,
  value: number,
  min: number,
  max: number,
  integerOnly = false,
): string | null => {
  const isValidNumber = integerOnly ? Number.isInteger(value) : Number.isFinite(value);

  if (!isValidNumber || value < min || value > max) {
    return t('adminMainPage.agent.idleState.errors.optionalRange', { min, max });
  }

  return null;
};

export const validateIdleStateDraft = (
  draft: ExpandedAgentIdleStateDraft,
  t: TFunction,
): IdleStateValidationResult => {
  const errors: IdleStateValidationErrors = {};
  const delayMs = Number(draft.delayMs);
  const customerReplyTimeoutSec = draft.customerReplyTimeoutSec.trim()
    ? Number(draft.customerReplyTimeoutSec)
    : null;
  const customerSilenceThresholdSec = draft.customerSilenceThresholdSec.trim()
    ? Number(draft.customerSilenceThresholdSec)
    : null;
  const customerMaxAttempts = draft.customerMaxAttempts.trim()
    ? Number(draft.customerMaxAttempts)
    : null;
  const messages = draft.messages.map((message) => message.trim()).filter(Boolean);
  const customerSilenceMessages = draft.customerSilenceMessages
    .map((message) => message.trim())
    .filter(Boolean);
  const customerEndCallMessages = draft.customerEndCallMessages
    .map((message) => message.trim())
    .filter(Boolean);

  if (draft.enabled) {
    if (
      !Number.isInteger(delayMs) ||
      delayMs < AGENT_IDLE_STATE_DELAY_MS_MIN ||
      delayMs > AGENT_IDLE_STATE_DELAY_MS_MAX
    ) {
      errors.delayMs = t('adminMainPage.agent.idleState.errors.range', {
        min: AGENT_IDLE_STATE_DELAY_MS_MIN,
        max: AGENT_IDLE_STATE_DELAY_MS_MAX,
      });
    }

    if (!messages.length) {
      errors.messages = t('adminMainPage.agent.idleState.errors.messagesRequired');
    }
  }

  if (customerReplyTimeoutSec !== null) {
    const error = getOptionalNumberError(
      t,
      customerReplyTimeoutSec,
      CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
      CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
    );
    if (error) errors.customerReplyTimeoutSec = error;
  }

  if (customerSilenceThresholdSec !== null) {
    const error = getOptionalNumberError(
      t,
      customerSilenceThresholdSec,
      CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
      CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
    );
    if (error) errors.customerSilenceThresholdSec = error;
  }

  if (customerMaxAttempts !== null) {
    const error = getOptionalNumberError(
      t,
      customerMaxAttempts,
      CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
      CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
      true,
    );
    if (error) errors.customerMaxAttempts = error;
  }

  if (
    customerReplyTimeoutSec !== null &&
    customerSilenceThresholdSec !== null &&
    customerSilenceThresholdSec >= customerReplyTimeoutSec
  ) {
    errors.customerSilenceThresholdSec = t(
      'adminMainPage.agent.idleState.errors.silenceThresholdRelation',
    );
  }

  if (Object.keys(errors).length > 0) {
    return { value: null, errors };
  }

  return {
    errors,
    value: {
      enabled: draft.enabled,
      delayMs: draft.enabled ? delayMs : AGENT_IDLE_STATE_DELAY_MS_MIN,
      messages,
      customerSilenceMessages,
      customerEndCallMessages,
      customerReplyTimeoutSec,
      customerSilenceThresholdSec,
      customerMaxAttempts,
    },
  };
};
